import type { Asset, AssetGroup, Scene } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import { TOOL_NAMES } from 'js/src/agent/toolNames';

import { resolveOverlayWordRangeOperation } from './overlay-placement';

import { setAssetIntroExitTimesOperation } from 'js/src/luminary/asset-timing-operations';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import {
    getRootAssetGroup,
    getVoiceDrivenAssetPlaybackRegions,
    getVoiceDrivenBaseAssetSourceDuration,
    isVoiceDrivenSceneGroup,
    translateTranscriptTimeToVideoTime,
} from 'js/src/luminary/luminary-selectors';
import {
    getVoicedrivenSceneEndTime,
    getVoicedrivenSceneStartTime,
} from 'js/src/luminary/voicedriven-scenes';

/** The agent-facing timing arguments shared by set_text and set_group_asset. */
export interface AssetTimingArgs {
    start_words?: string[];
    start_time_ms?: number;
}

/** True when either timing argument was supplied, so callers can skip the timing path entirely. */
export function hasAssetTimingArgs(args: AssetTimingArgs): boolean {
    return args.start_words !== undefined || args.start_time_ms !== undefined;
}

/**
 * An asset's resolved appearance window, in the units `setAssetIntroExitTimesOperation` consumes:
 * milliseconds from the start of the asset's own container (the scene, or its first subscene).
 * `exitTime` is the container's duration — the asset stays visible to the end of its scene.
 */
export type ResolvedAssetTiming = { introTime: number; exitTime: number } | { error: string };

/**
 * The rules a group imposes on its children's timing, checked before anything is written:
 * - a child never appears before any group it sits in (the group is the earliest any of its
 *   children can land), and
 * - the editable text and icon children of a group reveal in order, so an item cannot be timed
 *   earlier than an item above it that is already timed later.
 *
 * Returns a corrective message for the agent, or undefined when the timing is allowed. Shared
 * with the standalone recommend-timing tool so both speak the same rule.
 */
export function findGroupedAssetTimingConflict(
    asset: Asset,
    introTime: number,
): string | undefined {
    let parentGroup: AssetGroup | null = asset.getParentByType(VideoData.GROUP_ASSET_TYPE);
    while (parentGroup) {
        if (parentGroup.introAnimationOffset > introTime) {
            return `"${asset.name}" cannot appear before its group "${parentGroup.name}", which appears at ${Math.round(parentGroup.introAnimationOffset)}ms. Re-time the group first (${TOOL_NAMES.setGroupAsset}), or pick a later point.`;
        }
        parentGroup = parentGroup.getParentByType(VideoData.GROUP_ASSET_TYPE);
    }

    const rootGroup = getRootAssetGroup(asset);
    if (!rootGroup) {
        return undefined;
    }
    const siblings = getRevealableGroupChildren(rootGroup);
    const assetIndex = siblings.findIndex((sibling) => sibling.id === asset.id);
    for (let i = 0; i < assetIndex; i++) {
        const earlierSibling = siblings[i];
        if (earlierSibling.appliedIntroAnimationOffset > introTime) {
            return `"${asset.name}" must appear after "${earlierSibling.name}", which comes before it in the group and appears at ${Math.round(earlierSibling.appliedIntroAnimationOffset)}ms. Time the earlier items first, or pick a later point.`;
        }
    }
    return undefined;
}

/**
 * The children of a group that reveal in order: its editable text and icon assets, in tree order.
 * Icons are included because an icon-and-label pair is one item — an icon timed before the label
 * above it is the same defect as a label timed before the label above it.
 */
function getRevealableGroupChildren(group: AssetGroup): Asset[] {
    return group
        .getAssets()
        .filter(
            (child) =>
                (VideoData.isTextAsset(child) || VideoData.isIconAsset(child)) && child.isEditable,
        );
}

/**
 * The assets a timing request applies to. Timing a group means the whole group lands at once, so
 * the request fans out to the group and every leaf inside it: Luminary only lets a group's own
 * offset gate its children when the group carries an object animation, which scene-design groups
 * usually don't — writing the children directly is what makes the group actually move.
 */
export function assetsTimedTogether(asset: Asset): Asset[] {
    if (!VideoData.isAssetGroup(asset)) {
        return [asset];
    }
    return [asset, ...asset.getAssets().filter((child) => !VideoData.isAssetGroup(child))];
}

/**
 * Resolve an asset's requested appearance point without touching the video, so a tool can reject
 * a bad request before it applies any of its other edits.
 *
 * Two ways to express it, mirroring the two kinds of scene:
 * - `start_words`: consecutive transcript tokens (the same vocabulary `add_voiceover_scene` takes,
 *   resolved through the same {@link resolveOverlayWordRangeOperation}), so what the agent reads in
 *   the video state transcript is exactly what it can pass back. Voice-driven scenes only.
 * - `start_time_ms`: milliseconds from the start of the scene. Works on any scene, and is the only
 *   option for text-on-media scenes, which have no transcript to point at.
 *
 * Word matching returns transcript time. That is translated to video time through the scene
 * group's playback regions (so a cut before the word doesn't shift it), checked against the scene's
 * logical coverage (the `covers` range the agent sees), and finally made relative to the asset's
 * container — the same origin Luminary applies the offset from.
 */
export const resolveAssetTimingOperation = createLuminaryOperation(
    ({ call }, scene: Scene, asset: Asset, args: AssetTimingArgs): ResolvedAssetTiming => {
        const { start_words, start_time_ms } = args;

        if (start_words !== undefined && start_time_ms !== undefined) {
            return { error: 'Provide either start_words or start_time_ms, not both.' };
        }
        if (start_words === undefined && start_time_ms === undefined) {
            return { error: 'Provide either start_words or start_time_ms.' };
        }

        const container = asset.luminaryParent;
        if (!container) {
            return { error: `"${asset.name}" is not on a scene, so it cannot be timed.` };
        }
        if (VideoData.isSubscene(container) && container.index > 0) {
            return {
                error: `"${asset.name}" is on subscene ${container.index}, which already appears when that subscene starts. Only assets on a scene's first subscene can be timed.`,
            };
        }
        const containerDuration = container.duration;

        let introTime: number;
        if (start_time_ms !== undefined) {
            if (!Number.isFinite(start_time_ms)) {
                return { error: 'start_time_ms must be a number of milliseconds.' };
            }
            if (start_time_ms < 0) {
                return { error: 'start_time_ms cannot be negative.' };
            }
            if (start_time_ms >= containerDuration) {
                return {
                    error: `start_time_ms (${Math.round(start_time_ms)}ms) is longer than the scene itself (${Math.round(containerDuration)}ms), so the asset would never appear.`,
                };
            }
            introTime = start_time_ms;
        } else {
            const sceneGroup = scene.sceneGroup;
            if (!sceneGroup || !isVoiceDrivenSceneGroup(sceneGroup)) {
                return {
                    error: 'This scene is not part of a voice-driven scene group, so it has no transcript to time against. Use start_time_ms instead.',
                };
            }

            const range = call(resolveOverlayWordRangeOperation, sceneGroup, start_words!);
            if ('error' in range) {
                return range;
            }

            const wordVideoTime = translateTranscriptTimeToVideoTime(
                range.startTimeMillis,
                getVoiceDrivenAssetPlaybackRegions(sceneGroup),
                getVoiceDrivenBaseAssetSourceDuration(sceneGroup),
                sceneGroup.startTime,
            );
            if (
                wordVideoTime < getVoicedrivenSceneStartTime(scene) ||
                wordVideoTime >= getVoicedrivenSceneEndTime(scene)
            ) {
                return {
                    error: `The words "${start_words!.join(' ')}" are spoken outside this scene's time range, so the asset would never appear. Pick words this scene covers (see its \`covers\` field in the video state), or use start_time_ms.`,
                };
            }
            introTime = Math.min(
                Math.max(0, wordVideoTime - container.startTime),
                containerDuration,
            );
        }

        const conflict = findGroupedAssetTimingConflict(asset, introTime);
        if (conflict) {
            return { error: conflict };
        }

        return { introTime, exitTime: containerDuration };
    },
);

/** The outcome of applying resolved timing, phrased for the tool result. */
export type AppliedAssetTiming = { change: string } | { warning: string };

/**
 * Write a resolved appearance point, leaving the asset visible to the end of its container (the
 * same exit convention the standalone timing agent uses). Shared by set_text and set_group_asset
 * so both write through one path. Call it after the tool's other edits, so the asset already
 * carries its final content when the offsets are checkpointed.
 *
 * The container is re-read here because an edit in between (new text re-fitting a text-on-media
 * scene's duration) can shrink it past the requested point; that comes back as a warning for the
 * tool to report alongside its other changes rather than as a failed call.
 */
export const applyResolvedAssetTimingOperation = createLuminaryOperation(
    (
        { call },
        scene: Scene,
        asset: Asset,
        resolved: { introTime: number; exitTime: number },
        args: AssetTimingArgs,
        parentActionGroup: string,
    ): AppliedAssetTiming => {
        const containerDuration = asset.luminaryParent?.duration ?? resolved.exitTime;
        if (resolved.introTime >= containerDuration) {
            return {
                warning: `timing not applied: ${Math.round(resolved.introTime)}ms is past the scene's new duration (${Math.round(containerDuration)}ms)`,
            };
        }

        call(setAssetIntroExitTimesOperation, {
            timingSpecifications: assetsTimedTogether(asset).map((timedAsset) => ({
                sceneId: scene.id,
                assetId: timedAsset.id,
                introTime: resolved.introTime,
                exitTime: containerDuration,
            })),
            operatingOnMainLuminary: true,
            systemTriggered: false,
            parentActionGroup,
        });

        const when =
            args.start_words !== undefined
                ? `"${args.start_words.join(' ')}"`
                : `${Math.round(resolved.introTime)}ms into the scene`;
        return { change: `appears at ${when}` };
    },
);
